type ColorFormat = 'hex' | 'rgba';

type Rgba = { r: number; g: number; b: number; a: number };

const HEX_PATTERN = /^#?(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGBA_PATTERN = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*(\d*\.?\d+)\s*)?\)$/i;

const DEFAULT_FALLBACK: Record<ColorFormat, string> = {
  hex: '#000000',
  rgba: 'rgba(0, 0, 0, 1)',
};

const parseRgba = (value: string): Rgba | null => {
  const match = RGBA_PATTERN.exec(value);
  if (!match) return null;

  const [, red = '', green = '', blue = '', alpha] = match;
  const r = Number(red);
  const g = Number(green);
  const b = Number(blue);
  const a = alpha === undefined ? 1 : Number(alpha);
  if (r > 255 || g > 255 || b > 255 || a > 1) return null;

  return { r, g, b, a };
};

const normalizeColor = (value: string | null | undefined, format: ColorFormat): string | null => {
  if (!value) return null;

  if (format === 'rgba') {
    return parseRgba(value) ? value : null;
  }

  if (!HEX_PATTERN.test(value)) return null;
  return value.startsWith('#') ? value : `#${value}`;
};

const toHexChannel = (channel: number) => channel.toString(16).padStart(2, '0');

const rgbaToHex = ({ r, g, b }: Rgba) => `#${toHexChannel(r)}${toHexChannel(g)}${toHexChannel(b)}`;

const hexToRgbaString = (hex: string, alpha: number) => {
  const digits = hex.replace('#', '');
  const full = digits.length === 3 ? [...digits].map((digit) => digit + digit).join('') : digits;
  const channel = (offset: number) => Number.parseInt(full.slice(offset, offset + 2), 16);
  return `rgba(${channel(0)}, ${channel(2)}, ${channel(4)}, ${alpha})`;
};

export type { ColorFormat, Rgba };
export { DEFAULT_FALLBACK, hexToRgbaString, normalizeColor, parseRgba, rgbaToHex };
